import { useState, useEffect } from "react";
import { SelectedMap, FilterConfig } from "./Types";
import { FILTER_KEYS } from "./Carfilter";
import Image from "next/image";

// ─── Icons ────────────────────────────────────────────────────────────────────
const XIcon: React.FC<{ size?: number }> = ({ size = 16 }) =><Image
                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`}
                alt="corss icon"
                title="corss icon"
                width={size}
                height={size}
            />;

const FilterIcon: React.FC = () => <Image
                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}fillter-icon.svg`}
                alt="fillter icon"
                title="fillter icon"
                width={16}
                height={16}
            />;

const SearchIcon: React.FC = () => <Image
      src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/search.svg`}
      alt="search icon"
      title="search icon"
      width={16}
      height={16}
      className="invert-[60%] brightness-[70%]"
  />;

const CheckIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
    <Image
      src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}check.svg`}
      alt="check"
      title="check"
      width={size}
      height={size}
  />
);

// ─── Props ────────────────────────────────────────────────────────────────────
interface MobileFilterSheetProps {
  open: boolean;
  onClose: () => void;
  selectedMap: SelectedMap;
  onApply: (newMap: SelectedMap) => void;
  filterConfigs: FilterConfig[];
  clearAllTrackingClass?: string;
  applyFilterTrackingClass?: string;
}

// ─── Helper ───────────────────────────────────────────────────────────────────
function getTotalSelected(selectedMap: SelectedMap): number {
  return Object.values(selectedMap).reduce(
    (sum, arr) => sum + (arr?.length ?? 0),
    0,
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function MobileFilterSheet({
  open,
  onClose,
  selectedMap,
  onApply,
  filterConfigs,
  clearAllTrackingClass = '',
  applyFilterTrackingClass = '',
}: MobileFilterSheetProps) {
  const [activeSection, setActiveSection] = useState<string>("");
  const [brandSearch, setBrandSearch] = useState<string>("");
  const [localSelectedMap, setLocalSelectedMap] = useState<SelectedMap>(selectedMap);

  useEffect(() => {
    if (open) {
      setLocalSelectedMap(selectedMap);
    }
  }, [open, selectedMap]);

  useEffect(() => {
    if (filterConfigs.length > 0 && !activeSection) {
      setActiveSection(filterConfigs[0].key);
    }
  }, [filterConfigs, activeSection]);

  const getSelectedCount = (key: string): number =>
    (localSelectedMap[key] ?? []).length;

  const totalSelected = getTotalSelected(localSelectedMap);

  // Active section config
  const config = filterConfigs.find(f => f.key === activeSection);

  // Filter brand options by search query; all options for other sections
  const displayOptions =
    activeSection === FILTER_KEYS.BRAND && config
      ? config.options.filter((o) =>
          o.label.toLowerCase().trim().includes(brandSearch.toLowerCase().trim()),
        )
      : (config?.options ?? []);

  const handleOptionClick = (value: string): void => {
    if (!config) return;
    const current = localSelectedMap[config.key] ?? [];
    let updated: string[];

    if (config.type === "single") {
      updated = current[0] === value ? [] : [value];
    } else {
      updated = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
    }

    setLocalSelectedMap(prev => ({ ...prev, [config.key]: updated }));
  };

  const isChecked = (value: string): boolean =>
    (localSelectedMap[activeSection] ?? []).includes(value);

  const handleSectionSwitch = (key: string): void => {
    setActiveSection(key);
    setBrandSearch(""); // reset brand search when switching tabs
  };

  const handleClearLocalAll = () => {
    setLocalSelectedMap({});
    onApply({});
  };

  return (
    <>
      {/* ── Backdrop ──────────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 bg-[rgba(0,0,0,0.45)] z-[200] transition-[opacity] duration-300 ease-[ease] ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />

      {/* ── Bottom Sheet ──────────────────────────────────────────────────── */}
      <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-[20px] z-[201] max-h-[90vh] flex flex-col transition-[transform] duration-320 ease-[cubic-bezier(0.32,0,0.15,1)] ${open ? "[transform:translateY(0)]" : "[transform:translateY(100%)]"}`}>
        {/* Header */}
        <div className="flex items-center justify-between py-4 px-5 border-b border-b-[#f3f4f6] shrink-0">
          <p className="flex items-center gap-2 text-[17px] font-bold text-[#111] m-0">
            <FilterIcon />
            Filters
            {totalSelected > 0 && (
              <span className="inline-flex items-center justify-center bg-[#008C8B] text-white rounded-full w-5 h-5 text-[11px] font-bold shrink-0">{totalSelected}</span>
            )}
          </p>
          <button
            className="bg-transparent border-none cursor-pointer p-1 flex items-center text-[#6b7280]"
            onClick={onClose}
            type="button"
            aria-label="Close filters"
          >
            <Image
                src={`${process.env.NEXT_PUBLIC_CDN_STATIC_URL}lib-icon/cross-icon-new.svg`}
                alt="cross icon"
                title="cross icon"
                width={16}
                height={16}
                
            />
          </button>
        </div>

        {/* Clear All */}
        <button
          className={`mt-3 mx-5 w-[calc(100%-40px)] p-[11px] bg-[#fff5f5] border-[1.5px] border-[#fecaca] rounded-[10px] text-[#ef4444] text-[14px] font-semibold cursor-pointer shrink-0 ${clearAllTrackingClass}`}
          onClick={handleClearLocalAll}
          type="button"
        >
          Clear All Filters
        </button>

        {/* ── Body ─────────────────────────────────────────────────────────── */}
        <div className="flex flex-1 overflow-hidden mt-3">
          {/* Left Sidebar — section tabs */}
          <div className="w-[131px] shrink-0 overflow-y-auto bg-[#F9FAFB] rounded-tr-lg">
            {filterConfigs.map((section) => {
              const isActive = activeSection === section.key;
              const count = getSelectedCount(section.key);

              if(section.options.length === 0) return null;

              return (
                <div
                  key={section.key}
                  className={`py-[13px] px-3.5 text-[14px] font-normal cursor-pointer flex flex-col gap-0.5 transition-[background] duration-120 ease-[ease] ${
                    isActive ? "bg-[#008C8B] text-white rounded-lg mx-2" : "text-[#4A5565]"
                  }`}
                  onClick={() => handleSectionSwitch(section.key)}
                >
                  {section.title}
                  {count > 0 && (
                    <span
                      className={`text-[11px] font-normal ${
                        isActive ? "text-[rgba(255,255,255,0.8)]" : "text-[#99A1AF]"
                      }`}
                    >
                      {count} selected
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Right Content — options */}
          <div className="flex-1 overflow-y-auto pt-0 px-4 pb-2.5">
            {/* Brand search (brand section only) */}
            {activeSection === FILTER_KEYS.BRAND && (
              <div className="flex items-center gap-2 border-[1.8px] border-[#D1D5DC] rounded-lg py-2 px-3 mb-2.5">
                <SearchIcon />
                <input
                  className="border-none outline-none text-[13.5px] text-[#374151] flex-1 bg-transparent"
                  value={brandSearch}
                  onChange={(e) => setBrandSearch(e.target.value)}
                  placeholder="Search brand..."
                  autoComplete="off"
                />
              </div>
            )}

            {/* Hint label for single-select sections */}
            {config && config.type === "single" && (
              <p className="text-[11px] font-semibold text-[#9ca3af] uppercase tracking-[0.06em] mb-2">Select one option</p>
            )}

            {/* Options */}
            {displayOptions.length > 0 ? (
              displayOptions.map((opt) => {
                const checked = isChecked(opt.value);
                const isMulti = config?.type === "multi";

                return (
                  <div
                    key={opt.value}
                    className={`flex items-center justify-between py-[11px] px-1 border-b border-b-[#f9fafb] cursor-pointer rounded-md transition-[background] duration-100 ease-[ease] ${
                      checked ? "bg-[#f0fdfb]" : ""
                    }`}
                    onClick={() => handleOptionClick(opt.value)}
                  >
                    <div className="flex items-center gap-2.5">
                      {/* Radio or Checkbox */}
                      <span
                        className={`w-[18px] h-[18px] border-2 flex items-center justify-center shrink-0 transition-[border-color,background] duration-120 ease-[ease] ${
                          isMulti
                            ? "rounded-sm"
                            : "rounded-full"
                        } ${checked ? "text-white bg-[#008C8B] border-[#008C8B]" : "bg-white border-[#d1d5db]"}`}
                      >
                        {checked && <CheckIcon />}
                      </span>

                      <span className="text-[14px] text-[#1f2937] font-normal">
                        {opt.label}
                      </span>
                    </div>

                    {/* Count pill (brand & body type) */}
                    {opt.count !== undefined && (
                      <span className="text-[12px] text-[#9ca3af] bg-[#f3f4f6] py-0.5 px-2 rounded-[999px]">
                        ({opt.count})
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="py-5 text-[#9ca3af] text-[13px] text-center">
                {activeSection === "brand"
                  ? "No brands match your search"
                  : "No options available"}
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="py-3.5 px-5 border-t border-t-[#f3f4f6] shrink-0">
          <button
            className={`w-full py-2 px-3 bg-[#008C8B] text-white border-none rounded-lg text-[16px] font-normal cursor-pointer transition-[background] duration-180 ease-[ease] hover:bg-[#0b7c72] ${applyFilterTrackingClass}`}
            onClick={() => onApply(localSelectedMap)}
            type="button"
          >
            {totalSelected > 0
              ? `Apply Filters (${totalSelected})`
              : "Apply Filters"}
          </button>
        </div>
      </div>
    </>
  );
}
